import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const waterwaysData = [
  { river: 'Tapajós', '2010': 0.3, '2024': 36.0 },
  { river: 'Madeira', '2010': 20.0, '2024': 22.3 },
  { river: 'Amazonas', '2010': 5.0, '2024': 7.0 },
  { river: 'Paraguai', '2010': 15.0, '2024': 4.0 },
  { river: 'Paraná',  '2010': 25.0, '2024': 10.0 },
];

const fleetData = [
  { name: '2010', Balsa_Barcaça: 748,  Rebocador_Empurrador: 478, Lancha: 33,  Outros: 84 },
  { name: '2015', Balsa_Barcaça: 1356, Rebocador_Empurrador: 664, Lancha: 120, Outros: 190 },
  { name: '2020', Balsa_Barcaça: 1881, Rebocador_Empurrador: 757, Lancha: 202, Outros: 261 },
  { name: '2024', Balsa_Barcaça: 2398, Rebocador_Empurrador: 863, Lancha: 225, Outros: 302 },
];

export default function FleetAndWaterwaysSection() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const chartHeight = isMobile ? 260 : 300;

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Hidrovias e Frota Nacional</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A infraestrutura fluvial e os ativos que viabilizam o transporte aquaviário no Brasil.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Hidrovias */}
          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Ascensão do Rio Tapajós</h3>
            <Card>
              <CardContent className="p-2 pt-6">
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={waterwaysData} layout="vertical" margin={{ top: 8, right: 12, left: 6, bottom: 0 }}>
                    {/* X numérico (percentual) fica escondido; format no tooltip */}
                    <XAxis type="number" hide domain={[0, 40]} />
                    <YAxis
                      type="category"
                      dataKey="river"
                      width={isMobile ? 64 : 80}
                      stroke="#888888"
                      fontSize={12}
                    />
                    <Tooltip
                      cursor={{ fill: 'transparent' }} // remove a faixa cinza
                      formatter={(value) => [`${value}%`, '']}
                      labelFormatter={(label) => `Hidrovias — ${label}`}
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(6px)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} iconSize={10} />
                    <Bar dataKey="2010" fill="#0ea5e9" radius={[0, 4, 4, 0]} barSize={isMobile ? 10 : 14} />
                    <Bar dataKey="2024" fill="#0369a1" radius={[0, 4, 4, 0]} barSize={isMobile ? 10 : 14} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="mt-6 bg-white/80">
              <CardContent className="pt-6 text-gray-700 space-y-2 text-sm">
                <p>
                  A análise da origem das cargas na navegação interior revela uma transformação profunda. O <strong>Rio Tapajós</strong> saltou de
                  0,3% em 2010 para <strong>36% em 2024</strong>, tornando-se o principal eixo hidroviário do país.
                </p>
                <p>
                  O avanço acompanha a expansão do agronegócio e a consolidação de corredores logísticos multimodais pelo Arco Norte.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Frota */}
          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Evolução da Frota Interior</h3>
            <Card>
              <CardContent className="p-2 pt-6">
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <AreaChart data={fleetData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0369a1" stopOpacity={0.85} />
                        <stop offset="95%" stopColor="#0369a1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0891b2" stopOpacity={0.85} />
                        <stop offset="95%" stopColor="#0891b2" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="g3" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#a855f7" stopOpacity={0.85} />
                        <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="g4" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.85} />
                        <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                      </linearGradient>
                    </defs>

                    <XAxis dataKey="name" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis
                      stroke="#888888"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 100) / 10}k` : v)}
                      width={isMobile ? 30 : 40}
                    />
                    <Tooltip
                      cursor={{ fill: 'transparent' }}
                      formatter={(value, key) => [value.toLocaleString('pt-BR'), key.replace('_', ' ')]}
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(6px)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} iconSize={10} />

                    <Area type="monotone" dataKey="Balsa_Barcaça"            stackId="1" stroke="#0369a1" fill="url(#g1)" strokeWidth={2} dot={false} />
                    <Area type="monotone" dataKey="Rebocador_Empurrador"     stackId="1" stroke="#0891b2" fill="url(#g2)" strokeWidth={2} dot={false} />
                    <Area type="monotone" dataKey="Outros"                   stackId="1" stroke="#a855f7" fill="url(#g3)" strokeWidth={2} dot={false} />
                    <Area type="monotone" dataKey="Lancha"                   stackId="1" stroke="#f97316" fill="url(#g4)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="mt-6 bg-white/80">
              <CardContent className="pt-6 text-gray-700 space-y-2 text-sm">
                <p>
                  A frota da navegação interior cresceu <strong>182%</strong> desde 2010, alcançando <strong>3.788</strong> embarcações em 2024.
                </p>
                <p>
                  A maioria (63%) é composta por <strong>balsas e barcaças</strong>. O salto em <strong>rebocadores/empurradores</strong> (+80% desde 2010)
                  deu suporte direto à expansão do escoamento de granéis.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
